import { Link, useCanGoBack, useRouter } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2, Settings, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useGetClass } from '~entities/classes';
import { useGetWaitListSummary } from '~entities/classes/api/wait-list/get-wait-list-summary';
import { useUpdateWaitListStatusMutation } from '~entities/classes/api/wait-list/update-class-wait-list-status';
import type { StudentSearch } from '~entities/student/modal/student-search';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import {
  StudentSelector,
  type StudentSelectorOption,
} from '~features/students/ui/student-selector';

import { AddStudentToWaitlist } from '~pages/classes/wait-list/add-student-to-waitlist';

import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { isAgeInRange } from '~shared/utils/date-time';

import { WaitListCard } from './wait-list-card';

interface WaitListPageProps {
  classId: string;
}

export const WaitListPage = ({ classId }: WaitListPageProps) => {
  const router = useRouter();
  const canGoBack = useCanGoBack();

  const { hasAdminAccess } = useCurrentUserRole();
  const [enrollmentStudent, setEnrollmentStudent] = useState<StudentSearch>();
  const [enrollStudentDialogOpen, setEnrollStudentDialogOpen] = useState(false);

  const { data: classData } = useGetClass({ classId });
  const { data: waitListSummary, isFetching } = useGetWaitListSummary({
    classId,
  });

  const updateWaitListStatus = useUpdateWaitListStatusMutation();

  const handleUpdateWaitListStatus = async () => {
    try {
      await updateWaitListStatus.mutateAsync({
        classId,
        enabled: !classData?.maxWait,
      });
      toast.success(
        `Wait list ${classData?.maxWait ? 'disabled' : 'enabled'} successfully`,
      );
    } catch (error) {
      console.error(error);
      toast.error('Failed to update wait list status');
    }
  };

  // Handle student selection for enrollment
  const onStudentSelectionChange = (
    value: StudentSearch | undefined | null,
  ) => {
    if (value) {
      setEnrollStudentDialogOpen(true);
      setEnrollmentStudent(value);
    }
  };

  // Check if student is in age range for the class
  const isOptionDisabled = (option: StudentSelectorOption) => {
    if (!classData) return false;

    return !isAgeInRange({
      age: {
        years: option.ageYears,
        months: option.ageMonths,
      },
      startAge: {
        years: classData.startYear,
        months: classData.startMonth,
      },
      endAge: {
        years: classData.endYear,
        months: classData.endMonth,
      },
    });
  };

  return (
    <>
      <div className='mr-4 flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          {canGoBack ? (
            <Button
              variant={'ghost'}
              className='flex items-center p-2 [&_svg]:size-6'
              disabled={!canGoBack}
              onClick={() => {
                router.history.back();
              }}
            >
              <CircleArrowLeft className='size-6' />
            </Button>
          ) : (
            <Link
              to='/classes/$classId'
              params={{ classId }}
              className='flex w-fit items-center gap-2'
            >
              <CircleArrowLeft className='size-6' />
            </Link>
          )}
          <h2 className='text-2xl font-semibold'>
            {classData?.title} Wait List
          </h2>
        </div>
        {hasAdminAccess && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={!hasAdminAccess}>
              <Button
                variant={'ghost'}
                className='p-0 focus-visible:ring-0 [&_svg]:size-6'
              >
                <Settings />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={handleUpdateWaitListStatus}
                disabled={updateWaitListStatus.isPending}
              >
                {classData?.maxWait ? 'Disable' : 'Enable'} Wait List
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className='min-h-screen p-4 md:p-8'>
        <div className='mx-auto max-w-4xl'>
          {/* Header */}
          <div className='mb-8 grid grid-cols-3 gap-4'>
            <Card>
              <CardHeader>
                <CardTitle>Total Waitlist Students</CardTitle>
                <CardDescription className='text-xl font-bold'>
                  {waitListSummary?.waitlistCount}
                </CardDescription>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Total Seats in Class</CardTitle>
                <CardDescription className='text-xl font-bold'>
                  {classData?.maxSize}
                </CardDescription>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Seats open</CardTitle>
                <CardDescription className='text-xl font-bold'>
                  {classData?.remainingSeats}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          {hasAdminAccess && (
            <div className='mb-4 flex justify-end'>
              {/* Student Selector for individual enrollment */}
              <StudentSelector
                onChange={onStudentSelectionChange}
                isOptionDisabled={isOptionDisabled}
                placeholder='Add Student to Waitlist'
                studentCount={50}
              />
            </div>
          )}

          {isFetching ? (
            <div className='flex items-center justify-center py-16'>
              <Loader2 className='size-6 animate-spin' />
            </div>
          ) : (
            <div className='space-y-3'>
              {waitListSummary?.waitlistPositions.map((student, index) => (
                <WaitListCard
                  key={student.studentId}
                  student={student}
                  classData={classData}
                  isLastPosition={
                    index === waitListSummary.waitlistPositions.length - 1
                  }
                />
              ))}
            </div>
          )}
          {/* Empty State */}
          {waitListSummary?.waitlistPositions.length === 0 && (
            <div className='py-16 text-center'>
              <div className='mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-purple-200 to-pink-200'>
                <UserPlus className='size-8 text-purple-500' />
              </div>
              <h3 className='mb-2 text-lg font-medium text-gray-600'>
                No students on waitlist
              </h3>
              <p className='text-sm text-gray-500'>
                Students will appear here when they join the waitlist.
              </p>
            </div>
          )}
        </div>
      </div>
      {hasAdminAccess && (
        <AddStudentToWaitlist
          isOpen={enrollStudentDialogOpen}
          setIsOpen={setEnrollStudentDialogOpen}
          classId={classId}
          studentId={enrollmentStudent?._id.toString() ?? ''}
        />
      )}
    </>
  );
};
